import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useSnackbar } from 'notistack';
import { Button, CircularProgress } from '@mui/material';
import { auth } from '../../../firebase';
import { BASE_HOST, getAccountAPIHeader } from '../../../utils/urlConst';

const STATUS_URL = `${BASE_HOST}/api/basic/sync-has-offers/status`;
const SYNC_URL = `${BASE_HOST}/api/basic/sync-has-offers`;
const STATUS_POLL_MS = 15000;

const FINISHED = 'Offer sync finished.';
const NOT_SENT = ['Could not start the offer sync. Check your connection, sign in again if needed, and retry.', 'error'];
const DISCONNECTED = [
  'Lost the connection while waiting. The sync keeps running in the background; the Sync Offers button comes back when it finishes.',
  'warning',
];
const REFUSED = {
  403: ['Your account needs the operator role to sync offers.', 'error'],
  409: [
    'An offer sync is already running in the background. The Sync Offers button comes back when it finishes.',
    'info',
  ],
};

function failureNotice(error, sent) {
  if (!sent) return NOT_SENT;
  const status = error.response?.status;
  if (!status) return DISCONNECTED;
  return REFUSED[status] || [`Offer sync failed with HTTP ${status}.`, 'error'];
}

export default function SyncOffersButton() {
  const [user] = useAuthState(auth);
  const { enqueueSnackbar } = useSnackbar();
  const [running, setRunning] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const idToken = await user.getIdToken();
      const { data } = await axios.get(STATUS_URL, getAccountAPIHeader(idToken));
      setRunning(data.running === true);
    } catch (error) {
      console.warn('Offer sync status check failed', error.response?.status);
    }
  }, [user]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  const startSync = async () => {
    setRequesting(true);
    let sent = false;
    try {
      const idToken = await user.getIdToken();
      const request = axios.get(SYNC_URL, getAccountAPIHeader(idToken));
      sent = true;
      await request;
      enqueueSnackbar(FINISHED, { variant: 'success' });
    } catch (error) {
      const [message, variant] = failureNotice(error, sent);
      enqueueSnackbar(message, { variant });
    }
    await refresh();
    setRequesting(false);
  };

  const busy = running || requesting;
  return (
    <Button
      onClick={startSync}
      disabled={!user || busy}
      startIcon={busy ? <CircularProgress size={16} /> : null}
    >
      {busy ? 'Syncing in Background…' : 'Sync Offers'}
    </Button>
  );
}
